import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Row, Col, Card, Spinner } from 'react-bootstrap';
import { NavLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import moment from 'moment';

import api from './../../../api';
import ScanPointsList from '../../ScanPoints/ScanPointsList/ScanPointsList';
import EventPersonnelList from '../EventPersons/EventPersonnelList/EventPersonnelList';
import NotificationsList from '../../Notifications/NotificationsList/NotificationsList';
import EmailsList from '../../Emails/EmailsList/EmailsList';

import { getUploadsBaseUrl } from '../../../api/axiosInstance';

function EventOverview() {
    const { t } = useTranslation();
    const event = useSelector(state => state.event);
    const { eventID } = event;
    const { userID } = useSelector(state => state.user);
    const { partnerPortalSettings } = useSelector(state => state.partnerPortalSettings);
    const { contactInformationForPartners, logoMaterialID, isScanPointSalesEnabled } = partnerPortalSettings;
    const partnerID = localStorage.getItem("partnerID");

    const [ partner, setPartner ] = useState(null);

    let partnerLogoUrl = false;

    if (logoMaterialID && logoMaterialID.length) {
        partnerLogoUrl = `${getUploadsBaseUrl()}/uploads/materials/${localStorage.getItem('organizationID')}/${logoMaterialID}`;
    }

    useEffect(() => {
        if(partnerID && eventID) {
            api.partner.getEventPartner(eventID, partnerID).then(response => {
                setPartner(response.data.data.partners);
            })
        }
    }, [partnerID, eventID]);

    return (
        <React.Fragment>

            <h3>{t('events.event_summary')}</h3>

            <Row>
                <Col md={8}>
                    <Card>
                        <Card.Header>
                            {event.name}
                        </Card.Header>
                        <Card.Body>
                            {partner ?
                                <>
                                    {partnerLogoUrl ?
                                        <Row>
                                            <Col>
                                                <div className="d-flex justify-content-center">
                                                    <img
                                                        src={partnerLogoUrl}
                                                        alt="partner logo"
                                                        style={{ maxHeight: '150px', width: 'auto', marginBottom: '40px', marginRight: '20px' }}
                                                    />
                                                </div>
                                            </Col>
                                        </Row>
                                    : null}
                                    <Row>
                                        <Col sm={4}>
                                            <label className="label-block">{t('general.starts')}</label>
                                            {event.eventStarts ? 
                                                moment(event.eventStarts).format('DD.MM.YYYY HH:mm')
                                            : '-'}
                                        </Col>
                                        <Col sm={4}>
                                            <label className="label-block">{t('general.ends')}</label>
                                            {event.eventEnds ? 
                                                moment(event.eventEnds).format('DD.MM.YYYY HH:mm')
                                            : '-'}
                                        </Col>
                                    </Row>
                                    {event.venue ?
                                        <Row style={{ marginTop: '10px' }}>
                                            <Col sm={12}>
                                                <label className="label-block">{t('general.location')}</label>
                                                {event.venue.name}{event.venue.city && `, ${event.venue.city}`}{event.venue.streetAddress && `, ${event.venue.streetAddress}`}{event.venue.zip && `, ${event.venue.zip}`}
                                            </Col>
                                        </Row>
                                    : null}
                                    {event.description && event.description.length > 0 ?
                                        <Row style={{ marginTop: '30px' }}>
                                            <Col sm={12}>
                                                    <div
                                                        style={{ width: '100%' }}
                                                        dangerouslySetInnerHTML={{
                                                            __html: `
                                                                <style>
                                                                img { max-width: 100% }
                                                                p { word-break: break-word }
                                                                </style>
                                                                ${event.description}
                                                            `
                                                        }}
                                                    />
                                            </Col>
                                        </Row>
                                    : null}
                                </>
                            :
                                <Spinner animation="border" role="status" variant="primary" size="sm">
                                    <span className="sr-only">{t('general.loading')}</span>
                                </Spinner>
                            }
                        </Card.Body>
                    </Card>
                    <Card>
                        <Card.Header>
                            {t('events.organization_info')}
                            <NavLink to={`${eventID}/exhibitor`} className="btn btn-link btn-sm float-right">{t('general.edit')}</NavLink>
                        </Card.Header>
                        <Card.Body>
                            {partner ?
                                <React.Fragment>
                                    <Row>
                                        <Col sm={4}>
                                            <label className="label-block">{t('general.name')}</label>
                                            {partner.name || <span className="text-muted">{t('general.not_defined')}</span>}
                                        </Col>
                                        <Col sm={4}>
                                            <label className="label-block">Logo</label>
                                            {partner.logoMaterialID ? <span>{t('general.defined')}</span> : <span className="text-muted">{t('general.not_defined')}</span>}
                                        </Col>
                                        <Col sm={4}>
                                            <label className="label-block">{t('general.department')}</label>
                                            {partner.event.department || <span className="text-muted">{t('general.not_defined')}</span>}
                                        </Col>
                                    </Row>
                                    <Row style={{marginTop: '10px'}}>
                                        <Col sm={4}>
                                            <label className="label-block">{t('general.street_address')}</label>
                                            {partner.streetAddress || <span className="text-muted">{t('general.not_defined')}</span>}
                                        </Col>
                                        <Col sm={4}>
                                            <label className="label-block">{t('general.postal_code')}</label>
                                            {partner.postalCode || <span className="text-muted">{t('general.not_defined')}</span>}
                                        </Col>
                                        <Col sm={4}>
                                            <label className="label-block">{t('general.city')}</label>
                                            {partner.city || <span className="text-muted">{t('general.not_defined')}</span>}
                                        </Col>
                                    </Row>
                                </React.Fragment>
                            :  <Spinner animation="border" role="status" variant="primary" size="sm">
                                    <span className="sr-only">{t('general.loading')}</span>
                                </Spinner>
                            }
                        </Card.Body>
                    </Card>
                    <Card>
                        <Card.Header>
                            {t('events.scan_and_leads')}
                            <NavLink to={`${eventID}/scanPoints`} className="btn btn-link btn-sm float-right">{t('events.manage_scan_points')}</NavLink>
                        </Card.Header>
                        <ScanPointsList
                            eventID={eventID}
                            limit={10}
                            overView
                            isScanPointSalesEnabled={isScanPointSalesEnabled}
                        />
                    </Card>
                    <EventPersonnelList
                        eventID={eventID}
                        limit={10}
                    />
                    <EmailsList
                        eventID={eventID}
                        limit={5}
                    />
                </Col>
                <Col md={4}>
                   {contactInformationForPartners && contactInformationForPartners.length ?
                        <Card>
                            <Card.Header>
                                {t('general.contact_information')}
                            </Card.Header>
                            <Card.Body>
                                <div
                                    style={{ width: '100%' }}
                                    dangerouslySetInnerHTML={{
                                    __html: `
                                        <style>
                                        img { max-width: 100% }
                                        p { word-break: break-word }
                                        </style>
                                        ${contactInformationForPartners}
                                    `
                                    }}
                                />
                            </Card.Body>
                        </Card>
                    : null}
                    <NotificationsList
                        partnerID={partnerID}
                        userID={userID}
                        eventID={eventID}
                        limit={6}
                    />
                </Col>
            </Row>
        </React.Fragment>
    )
}

export default EventOverview;